import React, { useState, useEffect } from "react";
import { collection, getDocs, addDoc, deleteDoc, doc, updateDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../../firebase";
import AdminShell from "../../components/AdminShell";

const blank = { keywords: "", answer: "" };

export default function AdminChatbotFAQ() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const load = async () => {
    try {
      const snap = await getDocs(collection(db, "chatbot_faqs"));
      setItems(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (e) { console.error(e); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.keywords.trim() || !form.answer.trim()) { setMsg("Both fields required."); return; }
    setBusy(true); setMsg("");
    try {
      const data = {
        keywords: form.keywords.split(",").map((k) => k.trim().toLowerCase()).filter(Boolean),
        keywordsRaw: form.keywords,
        answer: form.answer.trim(),
        updatedAt: serverTimestamp()
      };
      if (editing === "new") {
        await addDoc(collection(db, "chatbot_faqs"), { ...data, createdAt: serverTimestamp() });
      } else {
        await updateDoc(doc(db, "chatbot_faqs", editing), data);
      }
      await load();
      setEditing(null); setForm(blank);
      setMsg("Saved successfully.");
    } catch (e) { setMsg("Error: " + e.message); }
    setBusy(false);
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this FAQ entry?")) return;
    await deleteDoc(doc(db, "chatbot_faqs", id));
    await load();
  };

  const startEdit = (item) => {
    setEditing(item.id);
    setForm({ keywords: item.keywordsRaw || item.keywords?.join(", ") || "", answer: item.answer });
  };

  return (
    <AdminShell title="Chatbot FAQ Manager">
      <div className="alert alert-info mb-16" style={{ fontSize: 13 }}>
        <i className="ti ti-robot"></i> Add keyword-answer pairs to train Goldie. When a user's message matches any keyword, the answer is shown. Separate keywords with commas.
      </div>
      {msg && <div className="alert alert-info">{msg}</div>}

      <div className="flex-between mb-16">
        <p className="text-muted" style={{ fontSize: 13 }}>{items.length} FAQ entries in the chatbot knowledge base.</p>
        {!editing && <button className="btn btn-primary" onClick={() => { setEditing("new"); setForm(blank); }}><i className="ti ti-plus"></i> Add New FAQ</button>}
      </div>

      {editing && (
        <div className="card mb-24">
          <h3 style={{ fontSize: 16, marginBottom: 16 }}>{editing === "new" ? "Add New FAQ Entry" : "Edit FAQ Entry"}</h3>
          <div className="form-group">
            <label className="form-label">Keywords (comma-separated) *</label>
            <input className="form-control" value={form.keywords} onChange={(e) => setForm({ ...form, keywords: e.target.value })}
              placeholder="e.g., fee, cost, magkano, how much" />
            <small className="text-muted">When the user types any of these words, this answer is shown.</small>
          </div>
          <div className="form-group">
            <label className="form-label">Answer *</label>
            <textarea className="form-control" rows={5} value={form.answer} onChange={(e) => setForm({ ...form, answer: e.target.value })}
              placeholder="Type the answer here..." />
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="btn btn-primary" onClick={save} disabled={busy}><i className="ti ti-check"></i> Save</button>
            <button className="btn btn-outline" onClick={() => { setEditing(null); setForm(blank); }}>Cancel</button>
          </div>
        </div>
      )}

      {items.length === 0 ? (
        <div className="card">
          <div className="empty-state"><i className="ti ti-robot-off"></i><div>No custom FAQ entries yet</div></div>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {items.map((item) => (
            <div key={item.id} className="card" style={{ padding: 16 }}>
              <div className="flex-between mb-8">
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {(item.keywords || []).slice(0, 6).map((k) => (
                    <span key={k} style={{ fontSize: 11, padding: "2px 8px", background: "var(--gold-100)", borderRadius: 999, color: "var(--gold-700)" }}>{k}</span>
                  ))}
                  {(item.keywords || []).length > 6 && <span style={{ fontSize: 11, color: "var(--ink-400)" }}>+{item.keywords.length - 6} more</span>}
                </div>
                <div style={{ display: "flex", gap: 6 }}>
                  <button className="btn btn-outline" style={{ fontSize: 12, padding: "4px 10px" }} onClick={() => startEdit(item)}><i className="ti ti-edit"></i></button>
                  <button className="btn btn-danger" style={{ fontSize: 12, padding: "4px 10px" }} onClick={() => remove(item.id)}><i className="ti ti-trash"></i></button>
                </div>
              </div>
              <p style={{ fontSize: 13, color: "var(--ink-700)", whiteSpace: "pre-line", margin: 0 }}>{item.answer}</p>
            </div>
          ))}
        </div>
      )}
    </AdminShell>
  );
}
